import { useEffect, useState } from "react";
import {
  isLateEntriesPeriod,
  isLateEntriesTagTitle,
  isEntriesClosedTagTitle,
  shouldShowEntriesClosedTag,
} from "./competitionEntries";
import {
  formatShowCalendarIso,
  getOrdinalSuffix,
  getShowDynamicCountdown,
  getShowToday,
  parseShowCalendarDate,
  sameShowCalendarDay,
} from "./showDates";

export { getOrdinalSuffix } from "./showDates";

export function getToday(testMode, testDate) {
  return testMode && testDate ? new Date(testDate) : new Date();
}

// Client Side Helpers

// Refine Results
export function refineResults(
  data,
  sortBy,
  searchTerm,
  activeCategories,
  pageBy,
  currentPage,
  collectionHandle,
  activeLocations,
  activeTypes,
  activeCompetitionTags
) {
  let refinedResults = [...data];

  refinedResults = data
    .sort((a, b) => {
      // If the sortBy value is "a-z", sort by title ascending
      if (sortBy === "a-z") {
        if (a.title.toLowerCase() < b.title.toLowerCase()) {
          return -1;
        } else {
          return 1;
        }
      }
      // If the sortBy value is "z-a", sort by title descending
      else if (sortBy === "z-a") {
        if (a.title.toLowerCase() > b.title.toLowerCase()) {
          return -1;
        } else {
          return 1;
        }
      }
      // If the sortBy value is "low-high", sort by price ascending
      else if (sortBy === "low-high") {
        switch (collectionHandle) {
          case "showbags":
            if (parseFloat(a.showbag_price) < parseFloat(b.showbag_price)) {
              return -1;
            } else {
              return 1;
            }
          case "rides":
            if (parseFloat(a.price) < parseFloat(b.price)) {
              return -1;
            } else {
              return 1;
            }
        }
      }
      // If the sortBy value is "high-low", sort by price descending
      else if (sortBy === "high-low") {
        if (collectionHandle == "showbags") {
          if (parseFloat(a.showbag_price) > parseFloat(b.showbag_price)) {
            return -1;
          } else {
            return 1;
          }
        } else if (collectionHandle == "rides") {
          if (parseFloat(a.price) > parseFloat(b.price)) {
            return -1;
          } else {
            return 1;
          }
        }
      }
      // If the sortBy value is "date", sort by start_date ascending
      else if (sortBy === "date") {
        if (collectionHandle == "events") {
          if (a.start_time < b.start_time) {
            return -1;
          } else {
            return 1;
          }
        }
      }
      // If the sortBy value is "time-grouped", sort by time and group by period
      else if (sortBy === "time-grouped") {
        if (collectionHandle === "events") {
          const timeA = convertTimeToMinutes(a.start_time);
          const timeB = convertTimeToMinutes(b.start_time);

          if (timeA === null && timeB === null) return 0;
          if (timeA === null) return 1;
          if (timeB === null) return -1;

          return timeA - timeB;
        }
      }
    })
    // Filter the results based on the searchTerm value
    .filter((item) => {
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();

        const titleMatches = item.title.toLowerCase().includes(searchTermLower);

        const categoriesMatch = item?.categories?.some((category) =>
          category?.title?.toLowerCase().includes(searchTermLower)
        );

        const exhibitorTypesMatch = item?.exhibitor_types?.some((type) =>
          type?.title?.toLowerCase().includes(searchTermLower)
        );

        const typesMatch = item?.event_type?.some((type) =>
          type?.title?.toLowerCase().includes(searchTermLower)
        );

        const locationsMatch = item?.locations?.some((location) =>
          location?.title?.toLowerCase().includes(searchTermLower)
        );

        const tagsMatch = item?.tags?.some((tag) =>
          tag?.title?.toLowerCase().includes(searchTermLower)
        );

        return (
          titleMatches ||
          categoriesMatch ||
          typesMatch ||
          locationsMatch ||
          exhibitorTypesMatch ||
          tagsMatch
        );
      } else {
        return item;
      }
    })
    // Filter the results based on the activeCategories value ( if item.showbag_categories[].title includes activeCategories) OR
    .filter((item) => {
      if (activeCategories?.length > 0) {
        return activeCategories?.some((category) => {
          if (collectionHandle === "commercial_exhibitors") {
            return item.exhibitor_types.some((itemCategory) => {
              return itemCategory.title === category;
            });
          }
          return item.categories.some((itemCategory) => {
            return itemCategory.title === category;
          });
        });
      } else {
        return item;
      }
    })
    .filter((item) => {
      if (activeLocations?.length > 0) {
        return activeLocations?.some((location) => {
          return item.locations.some((itemLocation) => {
            return itemLocation.title === location;
          });
        });
      } else {
        return item;
      }
    })
    .filter((item) => {
      if (activeTypes?.length > 0) {
        return activeTypes?.some((type) => {
          return item.event_type.some((itemType) => {
            return itemType.title === type;
          });
        });
      } else {
        return item;
      }
    })
    .filter((item) => {
      if (collectionHandle !== "competitions" || !activeCompetitionTags?.length) {
        return true;
      }
      return activeCompetitionTags.some(
        (tagTitle) =>
          item?.tags?.some((t) => t?.title === tagTitle) ||
          (isLateEntriesTagTitle(tagTitle) &&
            isLateEntriesPeriod(item?.entries_close, item?.late_entries_close)) ||
          (isEntriesClosedTagTitle(tagTitle) &&
            shouldShowEntriesClosedTag(
              item?.entries_open,
              item?.entries_close,
              item?.late_entries_close
            ))
      );
    })

    // Split the results into chunks based on the pageBy value (array of arrays)
    .reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / pageBy);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);

  // Add query params to the URL
  // if (typeof window !== "undefined") {
  //   let url = new URL(window.location.href);

  //   if (sortBy) {
  //     url.searchParams.set("sortBy", sortBy);
  //   } else {
  //     url.searchParams.delete("sortBy");
  //   }

  //   if (searchTerm === "") {
  //     url.searchParams.delete("searchTerm");
  //   } else {
  //     url.searchParams.set("searchTerm", searchTerm);
  //   }

  //   if (activeCategories.length > 0) {
  //     url.searchParams.set("activeCategories", activeCategories.join(","));
  //   } else {
  //     url.searchParams.delete("activeCategories");
  //   }

  //   if (pageBy) {
  //     url.searchParams.set("pageBy", pageBy);
  //   }

  //   if (currentPage > 1) {
  //     url.searchParams.set("currentPage", currentPage);
  //   } else {
  //     url.searchParams.delete("currentPage");
  //   }

  //   if (activeTypes?.length > 0) {
  //     url.searchParams.set("activeTypes", activeTypes.join(","));
  //   } else {
  //     url.searchParams.delete("activeTypes");
  //   }

  //   if (activeLocations?.length > 0) {
  //     url.searchParams.set("activeLocations", activeLocations.join(","));
  //   } else {
  //     url.searchParams.delete("activeLocations");
  //   }

  //   window.history.replaceState({}, "", url.toString());
  // }

  return refinedResults;
}
// Set Current Step On Slide Change
export function setCurrentStepOnSlideChange(setCurrent, api) {
  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
}

// Every Second Update the time
export function everySecondUpdateTheTime(updateTime, currentTime, intervalRef) {
  useEffect(() => {
    if (!currentTime) {
      updateTime(); // Initialize currentTime
    }

    intervalRef.current = setInterval(updateTime, 1000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [currentTime, updateTime]);
}

// Convert to AM PM Format
export function convertToAmPmFormat(timeString) {
  if (!timeString) return;
  // Splitting the time string into hours and minutes
  const [hours, minutes] = timeString?.split(":").map(Number);

  // Creating a new Date object with arbitrary date and parsed hours and minutes
  const date = new Date(2000, 0, 1, hours, minutes);

  // Getting the hours and minutes in 12-hour format
  const hours12 = date.getHours() % 12 || 12;

  // Determining whether it's AM or PM
  const period = date.getHours() < 12 ? "am" : "pm";

  // Formatting the time string in "11am" format
  if (minutes === 0) {
    return `${hours12}${period}`;
  } else {
    return `${hours12}:${String(minutes).padStart(2, "0")}${period}`;
  }
}

// Use is large screen
export function useIsLargeScreen() {
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    setIsLargeScreen(window.matchMedia("(min-width: 768px)").matches);

    // I write this into a function for better visibility
    const handleResize = (e) => {
      setIsLargeScreen(e.matches);
    };

    const mediaQuery = window.matchMedia("(min-width: 1024px)");

    mediaQuery.addEventListener("change", handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  return {
    isLargeScreen,
  };
}

// Get Default Tab Handle
export function getDefaultTabHandle(tabs) {
  // map through tabs and find the first tab with data.lenghth more the 0
  const defaultTab = tabs.find((tab) => tab.length > 0);
  return defaultTab?.handle ? defaultTab.handle : "food_outlets";
}

function convertTimeToMinutes(time) {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

// Get time period for grouping (Morning/Afternoon/Nightshift)
function getTimePeriod(time) {
  if (!time) return "Unknown";
  const minutes = convertTimeToMinutes(time);
  if (minutes === null) return "Unknown";
  // Morning: before 12:00 PM (720 minutes)
  // Afternoon: 12:00 PM to 7:00 PM (720 to 1140 minutes)
  // Nightshift: 7:00 PM and after (1140 minutes and after)
  if (minutes < 720) return "Morning";
  if (minutes < 1140) return "Afternoon";
  return "Nightshift";
}

// Group events by time periods using processed event times
export function groupEventsByTimePeriod(events, selectedDay, showDates) {
  const grouped = {
    Morning: [],
    Afternoon: [],
    Nightshift: [],
    Unknown: []
  };

  events.forEach(event => {
    // Get the actual times for this event on the selected day
    const dates = getDatesEventIsOn(event, showDates);
    const eventTimes = getEventTimesByDay(dates, selectedDay);

    if (eventTimes && eventTimes.length > 0) {
      // Check if event has any times
      const validTimes = eventTimes.filter(time =>
        time.start_time && time.start_time !== true && typeof time.start_time === 'string'
      );

            if (validTimes.length > 0) {
        // Categorize all start times into morning/afternoon/nightshift
        const morningTimes = [];
        const afternoonTimes = [];
        const nightshiftTimes = [];

        validTimes.forEach(time => {
          const period = getTimePeriod(time.start_time);
          if (period === "Morning") {
            morningTimes.push(time);
          } else if (period === "Afternoon") {
            afternoonTimes.push(time);
          } else if (period === "Nightshift") {
            nightshiftTimes.push(time);
          }
        });

        // Add event to appropriate groups
        if (morningTimes.length > 0) {
          grouped.Morning.push(event);
        }
        if (afternoonTimes.length > 0) {
          grouped.Afternoon.push(event);
        }
        if (nightshiftTimes.length > 0) {
          grouped.Nightshift.push(event);
        }

        // If no times were categorized (shouldn't happen with valid times), add to Unknown
        if (morningTimes.length === 0 && afternoonTimes.length === 0 && nightshiftTimes.length === 0) {
          grouped.Unknown.push(event);
        }
      } else if (eventTimes.some(time => time.all_day)) {
        // All day events go to Morning by default
        grouped.Morning.push(event);
      } else {
        // No valid times found
        grouped.Unknown.push(event);
      }
    } else {
      // No event times found
      grouped.Unknown.push(event);
    }
  });

  // Sort within each group by time
  Object.keys(grouped).forEach(period => {
    grouped[period].sort((a, b) => {
      const datesA = getDatesEventIsOn(a, showDates);
      const timesA = getEventTimesByDay(datesA, selectedDay);
      const datesB = getDatesEventIsOn(b, showDates);
      const timesB = getEventTimesByDay(datesB, selectedDay);

      // Get earliest start time for the current period for sorting
      const getEarliestTimeForPeriod = (times, targetPeriod) => {
        const validTimes = times
          ?.filter(time => time.start_time && time.start_time !== true && typeof time.start_time === 'string')
          ?.filter(time => getTimePeriod(time.start_time) === targetPeriod)
          ?.map(time => convertTimeToMinutes(time.start_time))
          ?.filter(time => time !== null);

        return validTimes && validTimes.length > 0 ? Math.min(...validTimes) : null;
      };

      const timeA = getEarliestTimeForPeriod(timesA, period);
      const timeB = getEarliestTimeForPeriod(timesB, period);

      if (timeA === null && timeB === null) return 0;
      if (timeA === null) return 1;
      if (timeB === null) return -1;

      return timeA - timeB;
    });
  });

  return grouped;
}

// Filter events by selected time period
export function filterEventsByTimePeriod(events, selectedTimePeriod, selectedDay, showDates) {
  if (selectedTimePeriod === "All") {
    return events;
  }

  return events.filter(event => {
    // Get the actual times for this event on the selected day
    const dates = getDatesEventIsOn(event, showDates);
    const eventTimes = getEventTimesByDay(dates, selectedDay);

    if (eventTimes && eventTimes.length > 0) {
      // Check if event has any times in the selected period
      const validTimes = eventTimes.filter(time =>
        time.start_time && time.start_time !== true && typeof time.start_time === 'string'
      );

      if (validTimes.length > 0) {
        // Check if any of the event's times fall in the selected period
        return validTimes.some(time => {
          const period = getTimePeriod(time.start_time);
          return period === selectedTimePeriod;
        });
      } else if (eventTimes.some(time => time.all_day)) {
        // All day events appear in Morning filter
        return selectedTimePeriod === "Morning";
      }
    }

    return false;
  });
}

// Get Filtered Events
export function getFilteredEvents(
  events,
  sortOrder,
  sortType,
  eventTypes,
  eventCategories,
  eventLocations
) {
  let filteredEvents = [...events];

  // Filter by event type
  if (eventTypes?.length > 0) {
    filteredEvents = filteredEvents.filter((event) => {
      return event?.event_type?.some((type) => eventTypes?.includes(type.slug));
    });
  }

  // Filter by event category
  if (eventCategories.length > 0) {
    filteredEvents = filteredEvents.filter((event) => {
      return event.categories.some((category) =>
        eventCategories.includes(category.slug)
      );
    });
  }

  // Filter by event location
  if (eventLocations.length > 0) {
    filteredEvents = filteredEvents.filter((event) => {
      return event.locations.some((location) =>
        eventLocations.includes(location.slug)
      );
    });
  }

  switch (sortType) {
    case "name":
      if (sortOrder === "ascending") {
        filteredEvents = filteredEvents.sort((a, b) => {
          if (a.title.toLowerCase() < b.title.toLowerCase()) {
            return -1;
          } else {
            return 1;
          }
        });
      } else {
        filteredEvents = filteredEvents.sort((a, b) => {
          if (a.title.toLowerCase() > b.title.toLowerCase()) {
            return -1;
          } else {
            return 1;
          }
        });
      }
      break;
    case "time":
      filteredEvents = filteredEvents.sort((a, b) => {
        const timeA = convertTimeToMinutes(a.start_time);
        const timeB = convertTimeToMinutes(b.start_time);

        if (timeA === null) return 1;
        if (timeB === null) return -1;
        if (timeA === timeB) return 0;

        if (sortOrder === "ascending") {
          return timeA - timeB;
        } else {
          return timeB - timeA;
        }
      });
      break;

    default:
      break;
  }

  return filteredEvents;
}

// Get Todays Events
export function getTodaysEvents(events, showDates, today) {
  const date = parseShowCalendarDate(today) ?? getShowToday();
  if (!showDates?.startDate || !showDates?.endDate) {
    return [];
  }

  const showStartDate = parseShowCalendarDate(showDates.startDate);
  const showEndDate = parseShowCalendarDate(showDates.endDate);
  if (!showStartDate || !showEndDate) {
    return [];
  }

  const todaysEvents = events.filter((event) => {
    if (event?.every_day) {
      return showStartDate <= date && showEndDate >= date;
    }
    if (event?.multi_day) {
      return event.days.some((day) => sameShowCalendarDay(day.date, date));
    }

    const startDate = parseShowCalendarDate(event?.start_date);
    const endDate = parseShowCalendarDate(event?.end_date);

    if (event?.recurrence?.value === "none") {
      return startDate && startDate <= date;
    } else if (event?.recurrence?.value === "daily") {
      return startDate && endDate && startDate <= date && endDate >= date;
    }
  });

  return todaysEvents;
}

// Get AMPM
export function getAmPm(currentTime) {
  return currentTime.getHours() >= 12 ? "pm" : "am";
}
// Get Hour
export function getHour(currentTime) {
  let hour = currentTime.getHours();
  if (hour === 12) {
    hour = 12;
  } else {
    hour = hour % 12;
  }
  return hour;
}

// Get Dates Event Is On
export function getDatesEventIsOn(event, showDates) {
  const datesWithTime = [];

  const { startDate: showStartDate, endDate: showEndDate } = showDates ?? {};

  if (event?.every_day) {
    const startDate = parseShowCalendarDate(showStartDate);
    const endDate = parseShowCalendarDate(showEndDate);

    if (!startDate || !endDate) {
      return datesWithTime;
    }

    const startTimeParts = event.start_time ? event.start_time.split(":") : [];
    const endTimeParts = event.end_time ? event.end_time.split(":") : [];

    const startHour =
      startTimeParts.length === 2 ? parseInt(startTimeParts[0]) : null;
    const startMinute =
      startTimeParts.length === 2 ? parseInt(startTimeParts[1]) : null;
    const endHour =
      endTimeParts.length === 2 ? parseInt(endTimeParts[0]) : null;
    const endMinute =
      endTimeParts.length === 2 ? parseInt(endTimeParts[1]) : null;

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      let start = new Date(currentDate);
      if (startHour !== null && startMinute !== null) {
        start.setHours(startHour, startMinute);
      }

      let end = new Date(currentDate);
      if (endHour !== null && endMinute !== null) {
        end.setHours(endHour, endMinute);
      }

      datesWithTime.push({
        date: formatDate(currentDate),
        showEndTime: !event.hide_end_time,
        start_time:
          startHour !== null && startMinute !== null
            ? formatTime(start)
            : null,
        end_time:
          endHour !== null && endMinute !== null ? formatTime(end) : null,
        all_day: event.all_day, // Include all_day property
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (event.multi_day && event.days && event.days.length > 0) {
    event.days.forEach((day) => {
      let dayDate = parseShowCalendarDate(day?.date);
      if (!dayDate) {
        return; // Skip this iteration
      }

      let start = new Date(dayDate);
      let end = new Date(dayDate);

      // If all_day is true, set start and end times to null
      if (day.all_day_multi_day) {
        start = null;
        end = null;
      } else {
        if (day.start_time) {
          const startTimeParts = day.start_time.split(":");
          if (
            startTimeParts.length !== 2 ||
            isNaN(parseInt(startTimeParts[0])) ||
            isNaN(parseInt(startTimeParts[1]))
          ) {
            return; // Skip this iteration
          }
          let startHour = parseInt(startTimeParts[0]);
          let startMinute = parseInt(startTimeParts[1]);
          start.setHours(startHour, startMinute);
        }

        if (day.end_time) {
          const endTimeParts = day.end_time.split(":");
          if (
            endTimeParts.length !== 2 ||
            isNaN(parseInt(endTimeParts[0])) ||
            isNaN(parseInt(endTimeParts[1]))
          ) {
            return; // Skip this iteration
          }
          let endHour = parseInt(endTimeParts[0]);
          let endMinute = parseInt(endTimeParts[1]);
          end.setHours(endHour, endMinute);
        }
      }

      datesWithTime.push({
        date: formatDate(dayDate),
        showEndTime: !day?.hide_end_time,
        start_time: day.all_day_multi_day
          ? true
          : day.start_time
          ? formatTime(start)
          : null,
        end_time: day.all_day_multi_day
          ? true
          : day.end_time
          ? formatTime(end)
          : null,
        all_day: day.all_day_multi_day, // Include all_day property
      });
    });
  } else {
    const startDate = parseShowCalendarDate(event.start_date);
    const endDate = parseShowCalendarDate(event.end_date);

    if (!startDate || !endDate) {
      return datesWithTime;
    }

    const startTimeParts = event.start_time ? event.start_time.split(":") : [];
    const endTimeParts = event.end_time ? event.end_time.split(":") : [];

    const startHour =
      startTimeParts.length === 2 ? parseInt(startTimeParts[0]) : null;
    const startMinute =
      startTimeParts.length === 2 ? parseInt(startTimeParts[1]) : null;
    const endHour =
      endTimeParts.length === 2 ? parseInt(endTimeParts[0]) : null;
    const endMinute =
      endTimeParts.length === 2 ? parseInt(endTimeParts[1]) : null;

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      let start = new Date(currentDate);
      if (startHour !== null && startMinute !== null) {
        start.setHours(startHour, startMinute);
      }

      let end = new Date(currentDate);
      if (endHour !== null && endMinute !== null) {
        end.setHours(endHour, endMinute);
      }

      datesWithTime.push({
        date: formatDate(currentDate),
        showEndTime: !event.hide_end_time,
        start_time:
          startHour !== null && startMinute !== null
            ? formatTime(start)
            : null,
        end_time:
          endHour !== null && endMinute !== null ? formatTime(end) : null,
        all_day: event.all_day, // Include all_day property
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return datesWithTime;
}

// Format Date
function formatDate(date) {
  return formatShowCalendarIso(date);
}

// Format Time
function formatTime(time) {
  const hours = time.getHours().toString().padStart(2, "0");
  const minutes = time.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

// Format Date Pretty
export function formatDatePretty(inputDate) {
  const date = parseShowCalendarDate(inputDate);
  if (!date) return "";

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const dayOfWeek = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayOfMonth = date.getDate();
  const suffix = getOrdinalSuffix(dayOfMonth);

  return `${dayOfWeek} ${dayOfMonth}${suffix} ${monthName}`;
}

// Get Dynamic Countdown
export function getDynamicCountdown(unformattedStartDate, startTime) {
  return getShowDynamicCountdown(unformattedStartDate, startTime);
}

export function getSubtitle(collectionHandle, data) {
  switch (collectionHandle) {
    case "showbags":
      return data?.showbag_price ? `$${data?.showbag_price}` : null;
    case "rides":
      return data?.price ? `$${data?.price}` : null;
    case "events":
      return data?.short_description;
    case "food_outlets":
      return null;
    case "commercial_exhibitors":
      return null;
    case "competitions":
      return null;
    default:
      return null;
  }
}

export function getShowAddToMyShow(collectionHandle) {
  switch (collectionHandle) {
    case "commercial_exhibitors":
    case "competitions":
    case "news":
      return false;
    default:
      return true;
  }
}

export function getFirstImage(collectionHandle, data) {
  let image;
  switch (collectionHandle) {
    case "showbags":
      image = data?.image;
      break;
    case "commercial_exhibitors":
      image = data?.grid_image;
      break;
    case "events":
      image = data?.grid_image;
      break;
    case "competitions":
      image = data?.grid_image;
      break;
    case "rides":
      image = data?.grid_image;
      break;
    case "food_outlets":
      image = data?.grid_image;
      break;
    case "news":
      image = data?.grid_image;
      break;
    default:
      image = data?.slideshow_images?.[0];
  }
  // Treat assets without a usable URL as missing (show rose placeholder)
  return image?.permalink ? image : null;
}

export function getPrice(collectionHandle, data) {
  switch (collectionHandle) {
    case "showbags":
      return data?.showbag_price ? `$${data?.showbag_price}` : null;
    case "rides":
      return data?.price ? `$${data?.price}` : null;
    default:
      return null;
  }
}

export function getValue(collectionHandle, data) {
  switch (collectionHandle) {
    case "showbags":
      return data?.showbag_value
        ? `$${parseInt(data?.showbag_value)} Value!`
        : null;
    default:
      return null;
  }
}

export function getCategoriesByDay(events) {
  return [
    ...new Set(
      events?.flatMap((event) =>
        event?.categories?.map((category) => category?.title)
      )
    ),
  ].sort();
}

export function getLocationsByDay(events) {
  return [
    ...new Set(
      events?.flatMap((event) =>
        event?.locations?.map((location) => location?.title)
      )
    ),
  ].sort();
}

export function getTypesByDay(events) {
  return [
    ...new Set(
      events?.flatMap((event) => event?.event_type?.map((type) => type?.title))
    ),
  ].sort();
}

export function getEventTimesByDay(dates, selectedDay) {
  if (selectedDay.slug == "all-days") {
    return dates;
  } else {
    return dates?.filter((day) =>
      sameShowCalendarDay(day.date, selectedDay.date)
    );
  }
}

export function getCategoriesByType(type, locations, facilities) {
  let categories = [];

  if (type === "attractions") {
    locations.forEach((location) => {
      location?.categories?.forEach((category) => {
        // Check if category exists in the categories array
        if (!categories.some((cat) => cat.value === category.value)) {
          categories.push({ value: category.value, label: category.label });
        }
      });
    });
  } else {
    facilities.forEach((facility) => {
      facility?.categories?.forEach((category) => {
        // Check if category exists in the categories array
        if (!categories.some((cat) => cat.value === category.value)) {
          categories.push({ value: category.value, label: category.label });
        }
      });
    });
  }

  return categories;
}

export function slugify(title) {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function toShowCalendarDateKey(value) {
  const date = parseShowCalendarDate(value);
  if (!date) return value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function pushGroupedEvent(acc, date, payload) {
  if (!date) return;
  if (!acc[date]) {
    acc[date] = [];
  }
  acc[date].push(payload);
}

/**
 * Group competition-linked events by Adelaide show day for Daily Schedule.
 * Expands every-day and date-range events across the show calendar when showDates is provided.
 */
export function getGroupEventsByDate(events, showDates) {
  return (events ?? []).reduce((acc, event) => {
    // Explicit multi-session sub events
    if (event?.multi_day && Array.isArray(event.days) && event.days.length > 0) {
      event.days.forEach((day) => {
        const date = toShowCalendarDateKey(day.date);
        pushGroupedEvent(acc, date, {
          ...event,
          date,
          hide_end_time: day.hide_end_time,
          start_time: day.start_time,
          end_time: day.end_time,
          override_title: day.override_title,
          override_short: day.override_short,
          override_display_data: day.override_display_data,
          all_day: day.all_day_multi_day,
          image: day.image,
        });
      });
      return acc;
    }

    // Every-day / date-range events (and any other shapes getDatesEventIsOn supports)
    const dates = getDatesEventIsOn(event, showDates);
    if (dates.length > 0) {
      dates.forEach((day) => {
        const date = toShowCalendarDateKey(day.date) || day.date;
        const startTime =
          day.start_time === true || day.start_time == null
            ? null
            : day.start_time;
        const endTime =
          day.end_time === true || day.end_time == null ? null : day.end_time;
        pushGroupedEvent(acc, date, {
          ...event,
          date,
          hide_end_time: !day.showEndTime,
          start_time: startTime,
          end_time: endTime,
          all_day: day.all_day,
        });
      });
      return acc;
    }

    // Legacy fallback: single date field
    const date = toShowCalendarDateKey(event.date);
    pushGroupedEvent(acc, date, event);
    return acc;
  }, {});
}
